import ScreenWrapper from "@/components/ScreenWrapper"
import { checkPermissions } from "@/utils/checkPermissions"
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import { useState } from "react"
import {
  Badge,
  ConfirmModal,
  DataTable,
  createActionsColumn,
  createCustomColumn,
  createTableSearchSchema,
  formatCurrencyAmount,
  formatDateToDutch,
  MultiLevelDropdownMenu,
  TABLE_PAGE_SIZE,
  usePaginatedTableWithUrlState,
  useExportMutation,
  type BadgeVariant,
} from "toptaal-shared"
import type { ColumnDef } from "@tanstack/react-table"
import { TableHeaderActions } from "@/components/data-table/TableHeaderActions"
import { facturationMenuItems } from "@/constants/navigation"
import { invoiceListQueryKeys } from "@/api/query-keys/invoices"
import {
  useInvoiceFilters,
  INVOICE_STORAGE_KEY,
} from "@/hooks/filters/useInvoiceFilters"
import {
  CREDITABLE_STATUSES,
  Invoice,
  InvoiceStatus,
} from "@/api/types/invoices"
import { useSendInvoiceToStudentMutation } from "@/api/mutations/invoices/useSendInvoiceToStudentMutation"
import { useCreditInvoiceMutation } from "@/api/mutations/invoices/useCreditInvoiceMutation"
import { useFinalizeInvoiceMutation } from "@/api/mutations/invoices/useFinalizeInvoiceMutation"
import { getInvoiceListRefetchInterval } from "@/api/utils/invoiceCache"

export const Route = createFileRoute("/facturatie/facturen/")({
  component: RouteComponent,
  beforeLoad: () => checkPermissions("read", "invoices"),
  validateSearch: createTableSearchSchema({
    defaultSortField: "invoice_date",
    defaultSortDirection: "desc",
  }),
})

const STATUS_BADGE_VARIANT: Record<InvoiceStatus, BadgeVariant> = {
  concept: "lightGrey",
  finalizing: "yellow",
  open: "blue",
  sent_to_student: "yellow",
  waiting_for_duo_export_batch: "yellow",
  waiting_for_duo_approval: "yellow",
  rejected_by_duo: "red",
  sent_to_twinfield: "blue",
  overdue: "red",
  paid: "green",
  credited: "lightGrey",
}

const ALL_COLUMN_IDS = [
  "invoiceNumber",
  "invoiceDate",
  "studentName",
  "totalLessonAmount",
  "totalLessonMaterialAmount",
  "status",
  "paymentSource",
]

function RouteComponent() {
  const searchParams = useSearch({ from: "/facturatie/facturen/" })
  const navigate = useNavigate({ from: "/facturatie/facturen/" })
  const [confirmInvoice, setConfirmInvoice] = useState<Invoice | null>(null)
  const [confirmCreditId, setConfirmCreditId] = useState<number | null>(null)
  const { mutate: sendInvoice } = useSendInvoiceToStudentMutation()
  const { mutate: finalizeInvoice } = useFinalizeInvoiceMutation()
  const { mutate: creditInvoice } = useCreditInvoiceMutation()

  const { filterItems, activeFilters, queryFn } = useInvoiceFilters({
    onPageReset: () =>
      navigate({ search: (prev) => ({ ...prev, page: 1 }), replace: true }),
  })

  const {
    isFetching,
    data,
    search,
    setSearch,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    pagination,
    columnVisibility,
    setColumnVisibility,
    resetColumnVisibility,
    mergedFilters,
    selectedColumns,
  } = usePaginatedTableWithUrlState({
    queryKey: [...invoiceListQueryKeys.all, activeFilters],
    queryFn,
    pageSize: TABLE_PAGE_SIZE,
    searchParams,
    navigate,
    storageKey: INVOICE_STORAGE_KEY,
    allColumnIds: ALL_COLUMN_IDS,
    staleTime: 0,
    refetchInterval: getInvoiceListRefetchInterval,
  })

  const columns: ColumnDef<Invoice>[] = [
    createCustomColumn({
      id: "invoiceNumber",
      accessorKey: "invoiceNumber",
      name: "Factuurnr.",
      cell: ({ row }: { row: { original: Invoice } }) =>
        row.original.invoiceNumber ?? "-",
    }),
    createCustomColumn({
      id: "invoiceDate",
      accessorKey: "invoiceDate",
      name: "Aanmaakdatum",
      cell: ({ row }: { row: { original: Invoice } }) =>
        formatDateToDutch(row.original.invoiceDate),
    }),
    createCustomColumn({
      id: "studentName",
      name: "Naam cursist",
      accessorKey: "studentName",
      cell: ({ row }: { row: { original: Invoice } }) => (
        <span>
          {row.original.studentName}{" "}
          <span className="text-primary-dark-blue-light">
            #{row.original.studentId}
          </span>
        </span>
      ),
    }),
    createCustomColumn({
      id: "lessonStartDate",
      name: "Periode",
      accessorKey: "lessonStartDate",
      cell: ({ row }: { row: { original: Invoice } }) => (
        <span>
          {formatDateToDutch(row.original.lessonStartDate)} -{" "}
          {formatDateToDutch(row.original.lessonEndDate)}
        </span>
      ),
    }),
    createCustomColumn({
      id: "totalLessonAmount",
      name: "Bedrag lessen",
      accessorKey: "totalLessonAmount",
      cell: ({ row }: { row: { original: Invoice } }) =>
        formatCurrencyAmount(Number(row.original.totalLessonAmount)),
    }),
    createCustomColumn({
      id: "totalLessonMaterialAmount",
      name: "Bedrag lesmateriaal",
      accessorKey: "totalLessonMaterialAmount",
      cell: ({ row }: { row: { original: Invoice } }) =>
        formatCurrencyAmount(Number(row.original.totalLessonMaterialAmount)),
    }),
    createCustomColumn({
      id: "status",
      name: "Status",
      accessorKey: "status",
      cell: ({ row }: { row: { original: Invoice } }) => (
        <Badge variant={STATUS_BADGE_VARIANT[row.original.status]}>
          {row.original.statusLabel}
        </Badge>
      ),
    }),
    createCustomColumn({
      id: "paymentSource",
      name: "Soort",
      accessorKey: "paymentSource",
      cell: ({ row }: { row: { original: Invoice } }) =>
        row.original.paymentSourceLabel,
    }),
    createActionsColumn([
      {
        label: "Concept versturen naar cursist",
        onClick: (e, row) => {
          e.stopPropagation()
          setConfirmInvoice(row)
        },
        hidden: ({ status, paymentSource }) =>
          status !== "concept" || paymentSource !== "duo",
      },
      {
        label: "Factuur definitief maken",
        onClick: (e, row) => {
          e.stopPropagation()
          setConfirmInvoice(row)
        },
        hidden: ({ status, paymentSource }) =>
          status !== "concept" || paymentSource !== "self",
      },
      {
        label: "Factuur crediteren",
        onClick: (e, row) => {
          e.stopPropagation()
          setConfirmCreditId(row.id)
        },
        hidden: ({ status, invoiceType }) =>
          !CREDITABLE_STATUSES.includes(status) ||
          invoiceType === "credit_invoice",
      },
    ]),
  ]

  const { mutate: exportInvoices } = useExportMutation({
    exportApiPrefix: "invoicing/invoices",
    filters: mergedFilters,
    selectedColumns,
    activeFilters,
  })

  const { mutate: exportDuoInvoices } = useExportMutation({
    exportApiPrefix: "invoicing/invoices",
    filters: mergedFilters,
    selectedColumns,
    activeFilters: { ...activeFilters, paymentSource: ["duo"] },
  })

  return (
    <ScreenWrapper
      showManagementMenu
      managementMenuItemsFromProps={facturationMenuItems}
    >
      <ConfirmModal
        open={confirmInvoice !== null}
        onOpenChange={(open) => !open && setConfirmInvoice(null)}
        title={
          confirmInvoice?.paymentSource === "self"
            ? "Factuur definitief maken"
            : "Factuur versturen naar cursist"
        }
        text={
          confirmInvoice?.paymentSource === "self"
            ? "Weet je zeker dat je deze factuur definitief wilt maken?"
            : "Weet je zeker dat je deze factuur naar de cursist wilt versturen?"
        }
        confirmText={
          confirmInvoice?.paymentSource === "self"
            ? "Definitief maken"
            : "Versturen"
        }
        cancelText="Annuleren"
        onConfirm={() => {
          if (!confirmInvoice) return

          const mutate =
            confirmInvoice.paymentSource === "self"
              ? finalizeInvoice
              : sendInvoice

          mutate(confirmInvoice.id, {
            onSettled: () => setConfirmInvoice(null),
          })
        }}
      />

      <ConfirmModal
        open={confirmCreditId !== null}
        onOpenChange={(open) => !open && setConfirmCreditId(null)}
        title="Factuur crediteren"
        text="Weet je zeker dat je deze factuur wilt crediteren? Er wordt een nieuwe creditfactuur aangemaakt."
        confirmText="Crediteren"
        cancelText="Annuleren"
        onConfirm={() => {
          if (confirmCreditId !== null) {
            creditInvoice(confirmCreditId, {
              onSettled: () => setConfirmCreditId(null),
            })
          }
        }}
      />

      <DataTable
        title="Facturen"
        data={data?.data.invoices || []}
        isLoading={isFetching}
        pagination={pagination}
        sortDirection={sortDirection}
        sortField={sortField}
        search={search}
        setSearch={setSearch}
        setSortField={setSortField}
        setSortDirection={setSortDirection}
        columns={columns}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        resetColumnVisibility={resetColumnVisibility}
        onRowClick={(_, row) =>
          navigate({
            to: "/facturatie/facturen/$invoiceId",
            params: { invoiceId: String(row.id) },
          })
        }
        headerLeftActions={<MultiLevelDropdownMenu items={filterItems} />}
        headerRightActions={
          <TableHeaderActions
            exportAction={{ onClick: exportInvoices }}
            customActions={[
              {
                label: "DUO export",
                onClick: exportDuoInvoices,
                variant: "secondary",
              },
            ]}
          />
        }
      />
    </ScreenWrapper>
  )
}
